import { Fragment, Suspense, lazy, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { can } from '@seller/shared';
import { api, type OffersDetail, type OfferLatest, type OfferSellerView } from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { Alert, Card, Spinner, TableScroll } from './ui.js';
import { SellerLink, SellerNameForm } from './Sellers.js';
import { WhenVisible } from './WhenVisible.js';
import { dateTime, day, integer, money } from '../format.js';

const OffersHistoryChart = lazy(() => import('../charts/OffersHistoryChart.js'));

/**
 * The competitor "Offers" card, shown on the SKU detail page and the Products
 * product view. It reads one ASIN's latest snapshot, its 90-day history, and the
 * seller presence, all from `GET /api/offers/:asin`. Amazon never reveals a
 * competitor's stock, so what this shows is who is on the listing right now and how
 * that has moved — a competitor disappearing in season is a likely stockout. The
 * history chart lazy-loads the charting library the way the sales charts do, so a
 * reader who never scrolls to it does not pay for Recharts. See docs/offers.md.
 *
 * Every seller id links to Amazon's public seller profile page and reads as the
 * owner's name for it when there is one; an importer can name a competitor inline
 * ("Name this seller"), and the full list lives on the Known sellers page.
 */

const BUY_BOX_LABEL: Record<OfferLatest['buyBoxHolder'], string> = {
  us: 'You',
  other: 'A competitor',
  none: 'No one',
  unknown: 'Unknown',
};

const OUR_PRESENCE_LABEL: Record<OfferLatest['ourPresence'], string> = {
  present: 'On the listing',
  'not-in-top-20': 'Not in top 20',
  absent: 'No offer',
  unknown: 'Unknown',
};

export function OffersCard({ asin }: { asin: string }) {
  const query = useQuery({
    queryKey: ['offers', asin],
    queryFn: () => api.get<OffersDetail>(`/api/offers/${encodeURIComponent(asin)}`),
  });

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-body">Offers</h2>
        <span className="font-mono text-xs text-muted">{asin}</span>
      </div>

      {query.isPending ? (
        <p className="mt-2 text-sm text-muted">Loading offers…</p>
      ) : query.isError ? (
        <div className="mt-2">
          <Alert>{query.error.message}</Alert>
        </div>
      ) : query.data.latest === null ? (
        <p className="mt-2 text-sm text-muted">
          Not polled yet. Poll competitor offers from the Imports page&rsquo;s Amazon connection
          card.
        </p>
      ) : (
        <OffersBody detail={query.data} />
      )}
    </Card>
  );
}

function OffersBody({ detail }: { detail: OffersDetail }) {
  const latest = detail.latest!;
  const ours = detail.offers.find((o) => o.isOurs);
  const ourPriceValue =
    latest.ourPresence === 'present'
      ? ours
        ? money(ours.landedPrice)
        : '—'
      : OUR_PRESENCE_LABEL[latest.ourPresence];
  return (
    <div className="mt-3 space-y-4">
      {(detail.competitorDisappeared || detail.newCompetitor) && (
        <div className="flex flex-wrap gap-2">
          {detail.competitorDisappeared && (
            <span
              className="rounded bg-warn-bg px-2 py-0.5 text-xs font-medium text-warn-text ring-1 ring-inset ring-warn-line"
              title="A seller on the listing in the last two weeks is gone from the latest poll — a likely stockout"
            >
              Competitor disappeared
            </span>
          )}
          {detail.newCompetitor && (
            <span
              className="rounded px-2 py-0.5 text-xs font-medium text-body ring-1 ring-inset ring-line"
              title="A seller first appeared on the listing within the last two weeks"
            >
              New competitor
            </span>
          )}
        </div>
      )}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        <Item label="Sellers" value={integer(latest.offersTotal)} />
        <Item
          label="FBA / MFN"
          value={`${integer(latest.offersFba ?? 0)} / ${integer(latest.offersMfn ?? 0)}`}
        />
        <Item
          label="Buy Box"
          value={
            latest.buyBoxHolder === 'other' && latest.buyBoxSellerId ? (
              <SellerLink
                sellerId={latest.buyBoxSellerId}
                name={latest.buyBoxSellerName}
                marketplaceId={detail.marketplaceId}
              />
            ) : (
              BUY_BOX_LABEL[latest.buyBoxHolder]
            )
          }
        />
        <Item label="Buy Box price" value={money(latest.buyBoxPrice)} />
        <Item label="Lowest FBA" value={money(latest.lowestFbaPrice)} />
        <Item label="Lowest overall" value={money(latest.lowestPrice)} />
        <Item label="Our price" value={ourPriceValue} />
        <Item label="As of" value={dateTime(latest.capturedAt)} />
      </dl>

      {detail.history.length > 1 && (
        <div className="border-t border-line pt-3">
          <WhenVisible>
            <Suspense fallback={<Spinner label="Loading offers chart" />}>
              <OffersHistoryChart history={detail.history} />
            </Suspense>
          </WhenVisible>
        </div>
      )}

      <SellerList sellers={detail.sellers} marketplaceId={detail.marketplaceId} />
    </div>
  );
}

function SellerList({
  sellers,
  marketplaceId,
}: {
  sellers: OfferSellerView[];
  marketplaceId: string;
}) {
  const session = useSession();
  const canName = session.status === 'signedIn' && can(session.user.role, 'data:import');
  // The seller whose name form is open, if any; one at a time.
  const [naming, setNaming] = useState<string | null>(null);

  if (sellers.length === 0) {
    return <p className="text-xs text-muted">No sellers recorded yet.</p>;
  }
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-body">Sellers seen</h3>
        <Link to="/offers/sellers" className="text-xs text-brand-600 hover:underline">
          Known sellers
        </Link>
      </div>
      <TableScroll>
        <table className="mt-2 w-full text-sm" aria-label="Offer sellers">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Seller</th>
              <th className="px-3 py-2 font-medium">Channel</th>
              <th className="px-3 py-2 text-right font-medium">Last price</th>
              <th className="px-3 py-2 font-medium">First seen</th>
              <th className="px-3 py-2 font-medium">Last seen</th>
              <th className="px-3 py-2 font-medium">On listing</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {sellers.map((s) => (
              <Fragment key={s.sellerId}>
                <tr className={s.present ? '' : 'text-muted'}>
                  <td className="px-3 py-1.5">
                    <SellerLink
                      sellerId={s.sellerId}
                      name={s.name}
                      isOurs={s.isOurs}
                      marketplaceId={marketplaceId}
                    />
                    {canName && !s.isOurs && naming !== s.sellerId && (
                      <button
                        type="button"
                        className="ml-2 text-[11px] text-brand-600 hover:underline"
                        onClick={() => setNaming(s.sellerId)}
                        aria-label={`${s.name ? 'Rename' : 'Name'} seller ${s.sellerId}`}
                      >
                        {s.name ? 'Rename' : 'Name this seller'}
                      </button>
                    )}
                    {s.lastSeenBuyBoxAt && (
                      <span
                        className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium text-muted ring-1 ring-inset ring-line"
                        title={`Last held the Buy Box ${dateTime(s.lastSeenBuyBoxAt)}`}
                      >
                        Buy Box
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">{s.isFba ? 'FBA' : 'MFN'}</td>
                  <td className="tabular px-3 py-1.5 text-right">{money(s.lastPrice)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">{day(s.firstSeenAt)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">{day(s.lastSeenAt)}</td>
                  <td className="px-3 py-1.5">
                    {s.present ? (
                      <span className="text-ok-text">On</span>
                    ) : (
                      <span
                        className="text-warn-text"
                        title="Not on the listing in the latest poll"
                      >
                        Gone
                      </span>
                    )}
                  </td>
                </tr>
                {naming === s.sellerId && (
                  <tr>
                    <td colSpan={6} className="px-3 pb-3">
                      <SellerNameForm sellerId={s.sellerId} onDone={() => setNaming(null)} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </TableScroll>
      <p className="mt-2 text-xs text-muted">
        A seller that was on the listing but is gone from the latest poll may have sold out — Amazon
        never reveals stock, so a disappearance in season is the closest signal.
      </p>
    </div>
  );
}

function Item({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-body">{value}</dd>
    </div>
  );
}
